import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import WorkspaceManage from "./WorkspaceManage";
import Usage from "./Usage";
import Billing from "./Billing";
import { ChevronRight, Link2, Activity, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/* Settings is the home for everything that is not an everyday destination.
   Four tabs, each a real address, so a link to a tab keeps working. */

const TABS = [
  { id: "general", label: "General" },
  { id: "team", label: "Team" },
  { id: "plan", label: "Plan & usage" },
  { id: "advanced", label: "Advanced" },
];

/* The app ships English copy with Bahasa Melayu available. The choice is
   stored locally and already drives the sign-in screen; the rest of the
   interface adopts it as each screen is translated. */
const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "ms", label: "Bahasa Melayu" },
];

function Row({ to, title, description, icon: Icon, external }) {
  const inner = (
    <>
      <span className="w-9 h-9 rounded-[10px] bg-black/[0.04] text-b2-inksoft flex items-center justify-center shrink-0">
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[14.5px] font-medium text-b2-ink">{title}</span>
        <span className="block text-[13px] text-b2-muted">{description}</span>
      </span>
      <ChevronRight className="w-4 h-4 text-b2-faint shrink-0" strokeWidth={1.8} />
    </>
  );
  const cls = "flex items-center gap-4 px-6 py-4 hover:bg-black/[0.02] transition-colors";
  return external
    ? <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
    : <Link to={to} className={cls}>{inner}</Link>;
}

export default function Settings() {
  const { tab: routeTab } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tab = TABS.some((t) => t.id === routeTab) ? routeTab : "general";
  const [planTab, setPlanTab] = useState("usage");
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("bicarra.lang") === "ms" ? "ms" : "en"; }
    catch { return "en"; }
  });

  const isAdmin = !!user?.is_platform_admin;

  const pickLang = (v) => {
    setLang(v);
    try { localStorage.setItem("bicarra.lang", v); } catch {}
    toast.success(v === "ms" ? "Bahasa disimpan" : "Language saved");
  };

  const TabButton = ({ id, label }) => (
    <button
      type="button"
      onClick={() => navigate(`/settings/${id}`)}
      data-testid={`settings-tab-${id}`}
      className={`relative h-10 px-0.5 mr-7 text-[14px] transition-colors ${
        tab === id ? "text-b2-ink font-semibold" : "text-b2-muted hover:text-b2-ink font-medium"
      }`}
    >
      {label}
      {tab === id && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-b2-indigo rounded-full" />}
    </button>
  );

  const SubTab = ({ id, label }) => (
    <button
      type="button" onClick={() => setPlanTab(id)}
      data-testid={`plan-tab-${id}`}
      className={`relative h-9 px-0.5 mr-6 text-[14px] transition-colors ${
        planTab === id ? "text-b2-ink font-semibold" : "text-b2-muted hover:text-b2-ink font-medium"
      }`}
    >
      {label}
      {planTab === id && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-b2-indigo rounded-full" />}
    </button>
  );

  return (
    <div data-testid="settings-page">
      <h1 className="text-[26px] leading-tight font-bold tracking-[-0.02em]">Settings</h1>

      <div className="mt-5 border-b border-b2-line flex items-center flex-wrap">
        {TABS.map((t) => <TabButton key={t.id} {...t} />)}
      </div>

      {tab === "general" && (
        <div className="mt-8 space-y-8 max-w-[640px]">
          <WorkspaceManage embedded section="general" />

          <div>
            <label htmlFor="lang" className="block text-[14px] font-medium text-b2-inksoft mb-1.5">
              Language
            </label>
            <select
              id="lang" value={lang} onChange={(e) => pickLang(e.target.value)}
              data-testid="settings-language"
              className="w-full max-w-[280px] h-11 px-3 rounded-[10px] bg-white border border-b2-linestrong text-[14px]
                         focus:outline-none focus:border-b2-indigo"
            >
              {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
            <p className="mt-1.5 text-[13px] text-b2-muted">
              Choose the language you feel most comfortable with. Sign-in follows this
              now; the rest of the interface follows as each screen is translated.
            </p>
          </div>

          <div className="pt-2 flex gap-8 text-[14px]">
            <Link to="/settings/team" className="text-b2-indigo hover:underline">Manage your team →</Link>
            <Link to="/settings/plan" className="text-b2-indigo hover:underline">Plan &amp; usage →</Link>
          </div>
        </div>
      )}

      {tab === "team" && (
        <div className="mt-8">
          <WorkspaceManage embedded section="team" />
        </div>
      )}

      {tab === "plan" && (
        <div className="mt-6">
          <div className="border-b border-b2-line flex items-center">
            <SubTab id="usage" label="Usage" />
            <SubTab id="billing" label="Billing" />
          </div>
          <div className="mt-7">
            {planTab === "usage" ? <Usage /> : <Billing />}
          </div>
        </div>
      )}

      {tab === "advanced" && (
        <div className="mt-8 space-y-7">
          <div>
            <h2 className="text-[20px] font-bold tracking-[-0.02em]">AI models</h2>
            <p className="mt-1 text-[14px] text-b2-muted max-w-[640px]">
              Bring your own account and Bicarra will use it. One OpenRouter key reaches
              most models from most major labs; connect a provider directly if you already
              have an account, or point Bicarra at a model you run yourself.
            </p>
            <div className="mt-5">
              <WorkspaceManage embedded section="api-keys" />
            </div>
          </div>

          <div>
            <h2 className="text-[20px] font-bold tracking-[-0.02em] mb-4">More</h2>
            <div className="b2-card overflow-hidden divide-y divide-b2-line">
              <Row
                to="/workspace/webhooks" icon={Link2}
                title="Connections"
                description="Send Bicarra updates to another service."
              />
              <Row
                to="/workspace/errorlog" icon={Activity}
                title="Service status"
                description="Understand what needs attention."
              />
              {isAdmin && (
                <Row
                  to="/admin/users" icon={ShieldCheck}
                  title="User management"
                  description="Administrator tools for all workspaces."
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
